import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Store } from 'lucide-react';
import Button from './Button';
import { useAuth } from '../context/AuthContext';
import { getStoreHours, setEmergencyStatus } from '../api/store';
import styles from './StoreStatusControl.module.css';

export default function StoreStatusControl() {
  const { user } = useAuth();
  const [emergencyClosed, setEmergencyClosed] = useState(false);
  const [closedReason, setClosedReason] = useState(null);
  const [reasonDraft, setReasonDraft] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const anchorRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const store = await getStoreHours(user.idToken);
      setEmergencyClosed(store.emergencyClosed);
      setClosedReason(store.emergencyReason);
    } catch {
      // Pill just keeps its last-known state until the next successful load.
    }
  }, [user.idToken]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onClick = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setPanelOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setPanelOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen]);

  const openPanel = () => {
    setError('');
    setReasonDraft('');
    setPanelOpen(true);
  };

  const confirmClose = async () => {
    setSubmitting(true);
    setError('');
    try {
      const store = await setEmergencyStatus({ closed: true, reason: reasonDraft.trim() || undefined }, user.idToken);
      setEmergencyClosed(store.emergencyClosed);
      setClosedReason(store.emergencyReason);
      setPanelOpen(false);
    } catch (err) {
      setError(err.message || 'Could not close the store.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmReopen = async () => {
    setSubmitting(true);
    setError('');
    try {
      const store = await setEmergencyStatus({ closed: false }, user.idToken);
      setEmergencyClosed(store.emergencyClosed);
      setClosedReason(store.emergencyReason);
      setPanelOpen(false);
    } catch (err) {
      setError(err.message || 'Could not reopen the store.');
    } finally {
      setSubmitting(false);
    }
  };

  const storeOpen = !emergencyClosed;

  return (
    <div className={styles.anchor} ref={anchorRef}>
      <button
        className={`${styles.pill} ${storeOpen ? styles.pillOpen : styles.pillClosed}`}
        onClick={openPanel}
        title={!storeOpen && closedReason ? `Closed: ${closedReason}` : undefined}
      >
        <span className={styles.dot} />
        {storeOpen ? 'Store Open' : 'Closed — Emergency'}
      </button>

      {panelOpen && (
        storeOpen ? (
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <span className={styles.warnIcon}><AlertTriangle size={16} /></span>
              <div>
                <p className={styles.panelTitle}>Close store immediately?</p>
                <p className={styles.panelText}>
                  New orders pause right away — customers will see the store as closed until you reopen it.
                  Orders already in progress are not affected.
                </p>
              </div>
            </div>

            <input
              type="text"
              className={styles.reasonInput}
              placeholder="Reason (optional) — e.g. Gas leak nearby"
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              disabled={submitting}
            />

            {error && <p className={styles.panelError}><AlertTriangle size={12} /> {error}</p>}

            <div className={styles.panelActions}>
              <Button variant="ghost" size="sm" onClick={() => setPanelOpen(false)} disabled={submitting}>Cancel</Button>
              <Button variant="danger" size="sm" onClick={confirmClose} disabled={submitting}>
                {submitting ? 'Closing…' : 'Close store now'}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <span className={styles.okIcon}><Store size={16} /></span>
              <div>
                <p className={styles.panelTitle}>Reopen store?</p>
                <p className={styles.panelText}>Customers will immediately be able to place new orders again.</p>
              </div>
            </div>

            {error && <p className={styles.panelError}><AlertTriangle size={12} /> {error}</p>}

            <div className={styles.panelActions}>
              <Button variant="ghost" size="sm" onClick={() => setPanelOpen(false)} disabled={submitting}>Cancel</Button>
              <Button variant="secondary" size="sm" onClick={confirmReopen} disabled={submitting}>
                {submitting ? 'Reopening…' : 'Reopen store'}
              </Button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
